import React, { useState, useEffect, useCallback } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell, PieChart, Pie, AreaChart, Area,
} from 'recharts';
import { DollarSign, TrendingUp, Activity, Wallet, Loader2, Download, FileText, Award, AlertTriangle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { focusToSolutionData } from '../health/HealthActionSpine';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function money(n) {
  if (n == null) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n)}`;
}

function Kpi({ label, value, sub, icon: Icon, tone, isDark, testId }) {
  return (
    <div className={cn('rounded-2xl border p-4', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200')} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', isDark ? 'text-slate-500' : 'text-slate-400')}>{label}</p>
        <Icon className="h-4 w-4" style={{ color: tone }} />
      </div>
      <p className={cn('text-2xl font-extrabold mt-1 tabular-nums', isDark ? 'text-slate-100' : 'text-slate-800')}>{value}</p>
      {sub && <p className={cn('text-[11px] mt-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>{sub}</p>}
    </div>
  );
}

export default function InvestmentView({ organizationId, isDark, navigate }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      const res = await authedFetch(`${API_URL}/api/lei-health/investment?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.ok ? await res.json() : null);
    } catch (e) { setData(null); } finally { setLoading(false); }
  }, [organizationId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-20" data-testid="investment-loading"><Loader2 className={cn('h-6 w-6 animate-spin', isDark ? 'text-slate-400' : 'text-slate-500')} /></div>;
  if (!data || !data.programs?.length) {
    return (
      <div className={cn('text-center py-16 rounded-2xl border', isDark ? 'bg-[#0d1526] border-[#22304d] text-slate-400' : 'bg-white border-slate-200 text-slate-500')} data-testid="investment-empty">
        <Wallet className="h-8 w-8 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No program spend found. Set a budget on your programs to see investment analytics.</p>
      </div>
    );
  }

  const t = data.totals;
  const bestValue = data.programs[0]; // sorted cheapest cost-per-point first
  const scatterData = data.programs.filter((p) => p.investment).map((p) => ({
    x: p.investment, y: p.overall_score, z: p.cost_per_lei_point || 0, name: p.name, roi: p.roi_multiple,
  }));
  const avgInv = scatterData.length ? scatterData.reduce((a, b) => a + b.x, 0) / scatterData.length : 0;
  const avgLei = scatterData.length ? scatterData.reduce((a, b) => a + b.y, 0) / scatterData.length : 0;
  const PIE = data.allocation_by_pillar;

  const downloadCsv = () => {
    const header = ['Program', 'Invested', 'LEI', 'Cost per LEI pt', 'Modeled value', 'ROI (x)', 'Cohorts reached'];
    const lines = data.programs.map((p) => [p.name, p.investment ?? '', p.overall_score, p.cost_per_lei_point ?? '', p.modeled_value ?? '', p.roi_multiple ?? '', p.cohorts_reached]);
    const csv = [header, ...lines].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'learning-investment.csv'; a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };
  const downloadPdf = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      const res = await authedFetch(`${API_URL}/api/lei-health/program-pillars/pdf?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = 'learning-investment.pdf'; a.click(); URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (e) { toast.error('Could not generate PDF'); }
  };

  const card = cn('rounded-2xl border p-5', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200');
  const maxAlloc = Math.max(...data.allocation_by_program.map((a) => a.investment), 1);
  const underperformers = scatterData.filter((p) => p.x > avgInv && p.y < avgLei);
  const budget = t.portfolio_budget;
  const budgetPct = budget ? Math.round((t.total_investment / budget) * 100) : null;
  const overspend = budget != null && t.total_investment > budget;
  const nearBudget = budget != null && !overspend && budgetPct >= 90;

  const reallocate = (p) => {
    if (!navigate) return;
    const sol = focusToSolutionData({
      engine: 'roi', entityLabel: `Reallocate spend: ${p.name}`, entityId: p.name, score: p.y, target: 100,
    });
    sol.financial_summary = { current_investment: p.x, cost_per_point: p.z, current_lei: p.y };
    sol.kpi.issue_statement = `${p.name} is high spend / low return (${money(p.x)} for LEI ${p.y}, ${money(p.z)}/pt). Model reallocating this budget to higher-ROI programs.`;
    navigate('/business-case-builder', { state: { solutionData: sol, currency: 'USD' } });
  };

  const setBudget = async () => {
    const val = window.prompt('Set the total learning portfolio budget ($). Leave blank to clear.', budget != null ? String(budget) : '');
    if (val === null) return;
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/lei-health/portfolio-budget`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget: val.trim() === '' ? null : Number(val.replace(/[^0-9.]/g, '')), organization_id: organizationId }),
      });
      if (!res.ok) throw new Error();
      toast.success('Portfolio budget updated'); fetchData();
    } catch (e) { toast.error('Could not update budget'); }
  };

  return (
    <div className="space-y-6" data-testid="investment-view">
      <div className="flex items-center justify-end gap-2">
        <button onClick={downloadCsv} className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid="investment-export-csv"><Download className="h-3.5 w-3.5" /> Export CSV</button>
        <button onClick={downloadPdf} className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border', isDark ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50')} data-testid="investment-export-pdf"><FileText className="h-3.5 w-3.5" /> Export PDF</button>
      </div>

      {/* KPI header */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total invested" value={money(t.total_investment)} sub={`${t.program_count} programs`} icon={DollarSign} tone="#10b981" isDark={isDark} testId="investment-kpi-total" />
        <Kpi label="Blended ROI" value={t.blended_roi != null ? `${t.blended_roi}x` : '—'} sub={`Modeled value ${money(t.total_modeled_value)}`} icon={TrendingUp} tone="#0ea5e9" isDark={isDark} testId="investment-kpi-roi" />
        <Kpi label="Avg cost / LEI pt" value={money(t.avg_cost_per_point)} sub="Lower = more efficient" icon={Activity} tone="#f59e0b" isDark={isDark} testId="investment-kpi-cost" />
        <Kpi label="Modeled value" value={money(t.total_modeled_value)} sub="Annual learning value" icon={Wallet} tone="#8b5cf6" isDark={isDark} testId="investment-kpi-value" />
      </div>

      {/* Budget vs actual */}
      <div className={card} data-testid="investment-budget">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Budget vs. actual spend</h3>
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              {budget != null ? <>Spent {money(t.total_investment)} of {money(budget)} ({budgetPct}%){overspend ? ' — over budget' : ''}</> : 'No portfolio budget set yet.'}
            </p>
          </div>
          <button onClick={setBudget} className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid="investment-set-budget"><Pencil className="h-3.5 w-3.5" /> {budget != null ? 'Edit budget' : 'Set budget'}</button>
        </div>
        {budget != null && (
          <div className={cn('h-3 rounded-full overflow-hidden relative', isDark ? 'bg-[#0b1220]' : 'bg-slate-100')}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, budgetPct)}%`, backgroundColor: overspend ? '#f43f5e' : '#10b981' }} />
          </div>
        )}
        {overspend && <p className="flex items-center gap-1.5 text-xs font-semibold text-rose-500 mt-2" data-testid="investment-overspend"><AlertTriangle className="h-3.5 w-3.5" /> Over budget by {money(t.total_investment - budget)}</p>}
        {nearBudget && <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-500 mt-2" data-testid="investment-nearbudget"><AlertTriangle className="h-3.5 w-3.5" /> Approaching budget — {budgetPct}% committed ({money(budget - t.total_investment)} left)</p>}
      </div>

      {/* Best value headline */}
      {bestValue?.cost_per_lei_point != null && (
        <button onClick={() => navigate && navigate('/lei-health/programs')} className={cn('w-full flex items-center gap-3 rounded-2xl border p-4 text-left', isDark ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15' : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100/70')} data-testid="investment-bestvalue">
          <span className={cn('shrink-0 w-10 h-10 rounded-xl flex items-center justify-center', isDark ? 'bg-emerald-500/20' : 'bg-emerald-100')}><Award className="h-5 w-5 text-emerald-500" /></span>
          <div><p className={cn('text-[11px] font-bold uppercase tracking-[0.14em]', isDark ? 'text-emerald-300/80' : 'text-emerald-700/80')}>Best value program</p>
          <p className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>{bestValue.name} — <span className="text-emerald-500">{money(bestValue.cost_per_lei_point)}</span> per LEI point ({bestValue.roi_multiple}x ROI)</p></div>
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scatter: spend vs LEI */}
        <div className={card} data-testid="investment-scatter">
          <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Spend vs. LEI lift</h3>
          <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Bottom-right = high spend, low return. Bubble size = cost per LEI point.</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 16, bottom: 24, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2a44' : '#eef2f7'} />
                <XAxis type="number" dataKey="x" name="Invested" tickFormatter={money} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} label={{ value: 'Invested', position: 'insideBottom', offset: -12, fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis type="number" dataKey="y" name="LEI" domain={[0, 100]} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} label={{ value: 'LEI', angle: -90, position: 'insideLeft', fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <ZAxis type="number" dataKey="z" range={[80, 500]} />
                <ReferenceLine x={avgInv} stroke={isDark ? '#475569' : '#cbd5e1'} strokeDasharray="4 4" />
                <ReferenceLine y={avgLei} stroke={isDark ? '#475569' : '#cbd5e1'} strokeDasharray="4 4" />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return <div className={cn('rounded-lg border p-2 text-xs', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}>
                    <p className="font-bold">{p.name}</p>
                    <p>Invested {money(p.x)} · LEI {p.y}</p>
                    <p>{money(p.z)}/pt · {p.roi}x ROI</p>
                  </div>;
                }} />
                <Scatter data={scatterData}>
                  {scatterData.map((e, i) => <Cell key={i} fill={e.y >= avgLei ? '#10b981' : '#f43f5e'} fillOpacity={0.7} />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          {underperformers.length > 0 && (
            <div className={cn('mt-3 rounded-xl border p-3', isDark ? 'bg-rose-500/10 border-rose-500/25' : 'bg-rose-50 border-rose-200')} data-testid="investment-underperformers">
              <p className={cn('flex items-center gap-1.5 text-xs font-bold', isDark ? 'text-rose-300' : 'text-rose-700')}><AlertTriangle className="h-3.5 w-3.5" /> Review: high spend, low LEI</p>
              <ul className={cn('mt-1 space-y-0.5 text-[11px]', isDark ? 'text-rose-200/80' : 'text-rose-700/90')}>
                {underperformers.map((p) => (
                  <li key={p.name} className="flex items-center justify-between gap-2">
                    <span>• <b>{p.name}</b> — {money(p.x)} for LEI {p.y} ({money(p.z)}/pt)</span>
                    <button onClick={() => reallocate(p)} className={cn('shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-md border', isDark ? 'border-rose-500/40 text-rose-200 hover:bg-rose-500/15' : 'border-rose-300 text-rose-700 hover:bg-rose-100')} data-testid={`investment-reallocate-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>Reallocate →</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Allocation by pillar (donut) */}
        <div className={card} data-testid="investment-allocation-pillar">
          <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Where the money goes</h3>
          <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Program spend allocated across the pillars each program targets.</p>
          <div className="flex items-center gap-4">
            <div className="h-56 w-56 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={PIE} dataKey="investment" nameKey="short_name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {PIE.map((e) => <Cell key={e.pillar_id} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={({ active, payload }) => active && payload?.length ? (
                    <div className={cn('rounded-lg border p-2 text-xs', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}>
                      <b>{payload[0].payload.short_name}</b>: {money(payload[0].payload.investment)} ({payload[0].payload.pct}%)
                    </div>) : null} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5">
              {PIE.map((p) => (
                <div key={p.pillar_id} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
                  <span className={cn('flex-1 truncate', isDark ? 'text-slate-300' : 'text-slate-600')}>{p.short_name}</span>
                  <span className={cn('tabular-nums font-semibold', isDark ? 'text-slate-200' : 'text-slate-700')}>{money(p.investment)}</span>
                  <span className={cn('tabular-nums w-8 text-right', isDark ? 'text-slate-500' : 'text-slate-400')}>{p.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modeled value over time */}
      {data.value_trend?.length > 1 && (
        <div className={card} data-testid="investment-value-trend">
          <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Modeled value over time</h3>
          <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Monthly modeled learning value vs. your total invested (flat reference).</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.value_trend} margin={{ top: 10, right: 16, left: 8, bottom: 4 }}>
                <defs>
                  <linearGradient id="mvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2a44' : '#eef2f7'} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis tickFormatter={money} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip content={({ active, payload }) => active && payload?.length ? (
                  <div className={cn('rounded-lg border p-2 text-xs', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}>
                    <b>{payload[0].payload.month}</b> · value {money(payload[0].payload.modeled_value)} · LEI {payload[0].payload.lei}
                  </div>) : null} />
                <ReferenceLine y={t.total_investment} stroke={isDark ? '#f59e0b' : '#d97706'} strokeDasharray="6 4" label={{ value: `Invested ${money(t.total_investment)}`, position: 'insideTopLeft', fontSize: 10, fill: isDark ? '#f59e0b' : '#d97706' }} />
                <Area type="monotone" dataKey="modeled_value" stroke="#8b5cf6" strokeWidth={2} fill="url(#mvGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Efficiency leaderboard */}
      <div className={card} data-testid="investment-leaderboard">
        <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Efficiency leaderboard</h3>
        <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Programs ranked by cost per LEI point — best value first.</p>
        <div className="space-y-1">
          {data.programs.map((p, i) => (
            <div key={p.id} className={cn('grid grid-cols-12 items-center gap-2 px-3 py-2.5 rounded-xl', i === 0 ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') : '')} data-testid={`investment-row-${p.id}`}>
              <span className={cn('col-span-1 text-sm font-bold tabular-nums', isDark ? 'text-slate-400' : 'text-slate-400')}>{i + 1}</span>
              <div className="col-span-4 min-w-0">
                <p className={cn('text-sm font-semibold truncate', isDark ? 'text-slate-100' : 'text-slate-800')}>{p.name}</p>
                <p className={cn('text-[11px]', isDark ? 'text-slate-500' : 'text-slate-400')}>{p.cohorts_reached} cohort{p.cohorts_reached === 1 ? '' : 's'} · LEI {p.overall_score}</p>
              </div>
              <div className="col-span-3">
                <div className={cn('h-2 rounded-full overflow-hidden', isDark ? 'bg-[#0b1220]' : 'bg-slate-100')}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, (p.investment || 0) / maxAlloc * 100)}%`, backgroundColor: i === 0 ? '#10b981' : (isDark ? '#38bdf8' : '#0ea5e9') }} />
                </div>
              </div>
              <span className={cn('col-span-2 text-right text-sm font-bold tabular-nums', isDark ? 'text-slate-200' : 'text-slate-700')}>{money(p.cost_per_lei_point)}<span className={cn('text-[10px] font-normal ml-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>/pt</span></span>
              <span className="col-span-2 text-right text-sm font-bold tabular-nums text-emerald-500">{p.roi_multiple != null ? `${p.roi_multiple}x` : '—'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
